import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isIos() {
  return /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
}

export function isStandalone() {
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service worker registered');
    return registration;
  } catch (e) {
    console.error('[Push] Service worker registration failed:', e);
    return null;
  }
}

// iOS subscriptions can silently die after ~1-2 weeks without getSubscription()
// returning null. Force a fresh unsubscribe+resubscribe periodically so dead
// endpoints don't linger unnoticed.
const RESUB_KEY = 'flowupy_push_last_refresh';
const RESUB_INTERVAL_MS = 6 * 24 * 60 * 60 * 1000; // 6 days

function shouldForceRefresh() {
  const last = Number(localStorage.getItem(RESUB_KEY) || 0);
  return Date.now() - last > RESUB_INTERVAL_MS;
}

function markRefreshed() {
  localStorage.setItem(RESUB_KEY, String(Date.now()));
}

export async function subscribeToPush(token) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('[Push] Push notifications not supported');
    return { ok: false, reason: 'unsupported' };
  }
  if (!token) {
    console.warn('[Push] No auth token provided');
    return { ok: false, reason: 'no-token' };
  }
  if (Notification.permission === 'denied') {
    console.warn('[Push] Notification permission denied');
    return { ok: false, reason: 'denied' };
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission:', permission);
    if (permission !== 'granted') {
      return { ok: false, reason: 'denied' };
    }

    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    const publicKey = data.publicKey;
    console.log('[Push] VAPID public key:', publicKey ? publicKey.substring(0, 20) + '...' : 'MISSING');
    if (!publicKey) {
      console.error('[Push] No VAPID public key from server');
      return { ok: false, reason: 'no-key' };
    }

    // Get or create subscription
    let subscription = await registration.pushManager.getSubscription();

    if (subscription && shouldForceRefresh()) {
      // Force a clean re-subscribe: iOS endpoints can go stale silently while
      // getSubscription() keeps returning the (now-dead) old object.
      console.log('[Push] Forcing subscription refresh (periodic health check)...');
      try {
        await subscription.unsubscribe();
      } catch (e) {
        console.warn('[Push] unsubscribe() before refresh failed (continuing):', e.message);
      }
      subscription = null;
    }

    if (!subscription) {
      console.log('[Push] Creating new subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      console.log('[Push] Subscription created:', subscription.endpoint.substring(0, 50) + '...');
    } else {
      console.log('[Push] Using existing subscription');
    }

    // Send subscription to server WITH auth token
    await axios.post(`${API}/push/subscribe`, 
      { subscription: subscription.toJSON() },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    markRefreshed();
    console.log('[Push] Subscription saved to server');
    return { ok: true };
  } catch (e) {
    console.error('[Push] Subscription failed:', e);
    return { ok: false, reason: 'error', error: e.message };
  }
}

let audioCtx = null;
export function playNotificationSound() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    // A bright two-note "ding-dong" chime, repeated twice for prominence
    const notes = [
      { f: 988, t: 0.0 },   // B5
      { f: 1319, t: 0.16 }, // E6
      { f: 988, t: 0.42 },
      { f: 1319, t: 0.58 },
    ];
    notes.forEach(({ f, t }) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.connect(g);
      g.connect(audioCtx.destination);
      o.type = 'triangle';
      o.frequency.setValueAtTime(f, now + t);
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(0.6, now + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.22);
      o.start(now + t);
      o.stop(now + t + 0.24);
    });
  } catch (e) {
    // ignore audio errors
  }
  // Haptic feedback on supported (mobile) devices
  try {
    if (navigator.vibrate) navigator.vibrate([180, 80, 180]);
  } catch (e) { /* ignore */ }
}
